import React, { useRef, useState } from "react";
import { MdPerson, MdPhotoCamera, MdVolunteerActivism, MdWork } from "react-icons/md";
import Button from "components/button";
import { InputField, Select, Textarea } from "components/fields";
import ChoiceOption from "views/public/shared/ChoiceOption";

const US_STATES = [
  { value: "AL", label: "Alabama" },
  { value: "AK", label: "Alaska" },
  { value: "AZ", label: "Arizona" },
  { value: "AR", label: "Arkansas" },
  { value: "CA", label: "California" },
  { value: "CO", label: "Colorado" },
  { value: "CT", label: "Connecticut" },
  { value: "DE", label: "Delaware" },
  { value: "FL", label: "Florida" },
  { value: "GA", label: "Georgia" },
  { value: "HI", label: "Hawaii" },
  { value: "ID", label: "Idaho" },
  { value: "IL", label: "Illinois" },
  { value: "IN", label: "Indiana" },
  { value: "IA", label: "Iowa" },
  { value: "KS", label: "Kansas" },
  { value: "KY", label: "Kentucky" },
  { value: "LA", label: "Louisiana" },
  { value: "ME", label: "Maine" },
  { value: "MD", label: "Maryland" },
  { value: "MA", label: "Massachusetts" },
  { value: "MI", label: "Michigan" },
  { value: "MN", label: "Minnesota" },
  { value: "MS", label: "Mississippi" },
  { value: "MO", label: "Missouri" },
  { value: "MT", label: "Montana" },
  { value: "NE", label: "Nebraska" },
  { value: "NV", label: "Nevada" },
  { value: "NH", label: "New Hampshire" },
  { value: "NJ", label: "New Jersey" },
  { value: "NM", label: "New Mexico" },
  { value: "NY", label: "New York" },
  { value: "NC", label: "North Carolina" },
  { value: "ND", label: "North Dakota" },
  { value: "OH", label: "Ohio" },
  { value: "OK", label: "Oklahoma" },
  { value: "OR", label: "Oregon" },
  { value: "PA", label: "Pennsylvania" },
  { value: "RI", label: "Rhode Island" },
  { value: "SC", label: "South Carolina" },
  { value: "SD", label: "South Dakota" },
  { value: "TN", label: "Tennessee" },
  { value: "TX", label: "Texas" },
  { value: "UT", label: "Utah" },
  { value: "VT", label: "Vermont" },
  { value: "VA", label: "Virginia" },
  { value: "WA", label: "Washington" },
  { value: "WV", label: "West Virginia" },
  { value: "WI", label: "Wisconsin" },
  { value: "WY", label: "Wyoming" },
];

const ROLES = [
  { value: "staff", label: "Staff", icon: MdWork },
  { value: "volunteer", label: "Volunteer", icon: MdVolunteerActivism },
];

const AVATAR_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/gif", "image/webp"];
const AVATAR_MAX_BYTES = 10 * 1024 * 1024;

const formatPhone = (raw) => {
  const digits = raw.replace(/\D/g, "").slice(0, 10);
  if (digits.length >= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  if (digits.length >= 3) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  if (digits.length > 0) return `(${digits}`;
  return "";
};

const validate = (formData, avatarFile) => {
  const errors = {};
  if (!formData.role) errors.role = "Please select your role";
  if (!formData.firstName.trim()) errors.firstName = "First name is required";
  if (!formData.lastName.trim()) errors.lastName = "Last name is required";
  if (!formData.email.trim()) {
    errors.email = "Email is required";
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
    errors.email = "Please enter a valid email address";
  }
  if (!formData.phone.trim()) {
    errors.phone = "Phone number is required";
  } else if (formData.phone.replace(/\D/g, "").length !== 10) {
    errors.phone = "Please enter a valid 10-digit phone number";
  }
  if (!formData.dob) errors.dob = "Date of birth is required";
  if (!formData.address.trim()) errors.address = "Street address is required";
  if (!formData.city.trim()) errors.city = "City is required";
  if (!formData.state) errors.state = "State is required";
  if (!formData.zip.trim()) {
    errors.zip = "ZIP code is required";
  } else if (!/^\d{5}(-\d{4})?$/.test(formData.zip)) {
    errors.zip = "Please enter a valid ZIP code";
  }
  if (!avatarFile) errors.avatar = "Profile photo is required";
  return errors;
};

const Section = ({ title, description, children }) => (
  <section>
    <h2 className="text-heading">{title}</h2>
    {description && <p className="mt-1 text-sm text-fg-muted">{description}</p>}
    <div className="mt-4 flex flex-col gap-4">{children}</div>
  </section>
);

const ProfileSetupStep = ({ organizationName, onComplete, initialData }) => {
  const formRef = useRef(null);
  const fileInputRef = useRef(null);
  const [avatarPreview, setAvatarPreview] = useState(initialData?.avatarPreview || null);
  const [avatarFile, setAvatarFile] = useState(initialData?.avatar || null);
  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState({
    role: initialData?.role || "",
    firstName: initialData?.firstName || "",
    lastName: initialData?.lastName || "",
    email: initialData?.email || "",
    phone: initialData?.phone || "",
    jobTitle: initialData?.jobTitle || "",
    dob: initialData?.dob || "",
    address: initialData?.address || "",
    city: initialData?.city || "",
    state: initialData?.state || "",
    zip: initialData?.zip || "",
    hobbies: initialData?.hobbies || "",
    favoriteFood: initialData?.favoriteFood || "",
    interests: initialData?.interests || "",
  });

  const setField = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
  };

  const handleInputChange = (e) => setField(e.target.name, e.target.value);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!AVATAR_TYPES.includes(file.type)) {
      setErrors((prev) => ({ ...prev, avatar: "Please upload a valid image file (JPG, PNG, GIF, or WebP)" }));
      return;
    }
    if (file.size > AVATAR_MAX_BYTES) {
      setErrors((prev) => ({ ...prev, avatar: "Image must be less than 10MB" }));
      return;
    }

    setAvatarFile(file);
    setErrors((prev) => ({ ...prev, avatar: null }));

    const reader = new FileReader();
    reader.onload = (event) => setAvatarPreview(event.target.result);
    reader.readAsDataURL(file);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const nextErrors = validate(formData, avatarFile);
    setErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      // Wait for the error render, then bring the first invalid control into view.
      requestAnimationFrame(() => {
        const firstInvalid = formRef.current?.querySelector('[aria-invalid="true"], [data-invalid="true"]');
        if (!firstInvalid) return;
        firstInvalid.scrollIntoView({ behavior: "smooth", block: "center" });
        const focusTarget = firstInvalid.matches("input, select, textarea, button")
          ? firstInvalid
          : firstInvalid.querySelector("input, select, textarea, button");
        focusTarget?.focus({ preventScroll: true });
      });
      return;
    }

    onComplete({ ...formData, avatar: avatarFile });
  };

  return (
    <form ref={formRef} noValidate onSubmit={handleSubmit}>
      <div className="text-center">
        <h1 className="text-title">Set Up Your Profile</h1>
        <p className="mt-2 text-sm leading-relaxed text-fg-muted">
          {organizationName
            ? `Let ${organizationName} know a little about you before your assessment.`
            : "Tell us a bit about yourself before your assessment."}
        </p>
      </div>

      <div className="mt-8 flex flex-col gap-8">
        <Section title="What's Your Role?">
          <div
            role="group"
            aria-label="Role"
            data-invalid={errors.role ? true : undefined}
            aria-describedby={errors.role ? "profile-role-error" : undefined}
            className="grid grid-cols-2 gap-3"
          >
            {ROLES.map(({ value, label, icon: Icon }) => (
              <ChoiceOption
                key={value}
                selected={formData.role === value}
                onClick={() => setField("role", value)}
                leading={<Icon aria-hidden="true" className="h-5 w-5 shrink-0 text-fg-muted" />}
                className={errors.role ? "border-danger" : undefined}
              >
                {label}
              </ChoiceOption>
            ))}
          </div>
          {errors.role && (
            <p id="profile-role-error" className="-mt-2 text-xs text-danger-fg">
              {errors.role}
            </p>
          )}
        </Section>

        <Section title="Profile Photo">
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              aria-label={avatarPreview ? "Change profile photo" : "Upload profile photo"}
              data-invalid={errors.avatar ? true : undefined}
              aria-describedby={errors.avatar ? "profile-avatar-hint profile-avatar-error" : "profile-avatar-hint"}
              className={`group relative flex h-24 w-24 shrink-0 items-center justify-center overflow-hidden rounded-full border-2 bg-surface-raised transition-colors focus-ring ${
                errors.avatar ? "border-danger" : avatarPreview ? "border-primary" : "border-line hover:border-accent"
              }`}
            >
              {avatarPreview ? (
                <img src={avatarPreview} alt="" className="h-full w-full object-cover" />
              ) : (
                <MdPerson aria-hidden="true" className="h-10 w-10 text-fg-muted" />
              )}
              <span
                aria-hidden="true"
                className="absolute inset-0 flex items-center justify-center bg-overlay/40 text-white opacity-0 transition-opacity group-hover:opacity-100"
              >
                <MdPhotoCamera className="h-6 w-6" />
              </span>
            </button>
            <div className="flex-1">
              <Button variant="secondary" size="sm" onClick={() => fileInputRef.current?.click()}>
                {avatarPreview ? "Change Photo" : "Upload Photo"}
              </Button>
              <p id="profile-avatar-hint" className="mt-1.5 text-xs text-fg-muted">
                JPG, PNG, GIF, or WebP. Max 10MB.
              </p>
              {errors.avatar && (
                <p id="profile-avatar-error" role="alert" className="mt-1 text-xs text-danger-fg">
                  {errors.avatar}
                </p>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
              onChange={handleFileChange}
              className="hidden"
              tabIndex={-1}
            />
          </div>
        </Section>

        <Section title="Basic Information">
          <div className="grid grid-cols-2 gap-4">
            <InputField
              label="First Name"
              name="firstName"
              autoComplete="given-name"
              required
              value={formData.firstName}
              onChange={handleInputChange}
              error={errors.firstName}
              placeholder="John"
            />
            <InputField
              label="Last Name"
              name="lastName"
              autoComplete="family-name"
              required
              value={formData.lastName}
              onChange={handleInputChange}
              error={errors.lastName}
              placeholder="Doe"
            />
          </div>
          <InputField
            label="Email"
            type="email"
            name="email"
            autoComplete="email"
            required
            value={formData.email}
            onChange={handleInputChange}
            error={errors.email}
            placeholder="john.doe@email.com"
          />
          <InputField
            label="Phone"
            type="tel"
            name="phone"
            autoComplete="tel"
            required
            value={formData.phone}
            onChange={(e) => setField("phone", formatPhone(e.target.value))}
            error={errors.phone}
            placeholder="(555) 123-4567"
          />
          <InputField
            label="Date of Birth"
            type="date"
            name="dob"
            autoComplete="bday"
            required
            value={formData.dob}
            onChange={handleInputChange}
            error={errors.dob}
          />
          <InputField
            label="Job Title / Ministry Role"
            name="jobTitle"
            value={formData.jobTitle}
            onChange={handleInputChange}
            placeholder="e.g., Youth Leader, Greeter, etc."
          />
        </Section>

        <Section title="Address">
          <InputField
            label="Street Address"
            name="address"
            autoComplete="street-address"
            required
            value={formData.address}
            onChange={handleInputChange}
            error={errors.address}
            placeholder="123 Main Street"
          />
          <div className="grid grid-cols-2 gap-4">
            <InputField
              label="City"
              name="city"
              autoComplete="address-level2"
              required
              value={formData.city}
              onChange={handleInputChange}
              error={errors.city}
              placeholder="Dallas"
            />
            <Select
              label="State"
              name="state"
              autoComplete="address-level1"
              required
              placeholder="Select State"
              options={US_STATES}
              value={formData.state}
              onChange={handleInputChange}
              error={errors.state}
            />
          </div>
          <InputField
            label="ZIP Code"
            name="zip"
            autoComplete="postal-code"
            inputMode="numeric"
            required
            value={formData.zip}
            onChange={handleInputChange}
            error={errors.zip}
            placeholder="75001"
            maxLength={10}
            className="w-1/2"
          />
        </Section>

        <Section
          title="Preferences"
          description="Optional: help leadership know how to appreciate and support you."
        >
          <Textarea
            label="Favorite Hobbies & Activities"
            name="hobbies"
            rows={2}
            value={formData.hobbies}
            onChange={handleInputChange}
            placeholder="e.g., Hiking, reading, playing guitar…"
          />
          <Textarea
            label="Favorite Food & Restaurants"
            name="favoriteFood"
            rows={2}
            value={formData.favoriteFood}
            onChange={handleInputChange}
            placeholder="e.g., Italian food, Chick-fil-A, sushi…"
          />
          <Textarea
            label="Other Interests & Gift Preferences"
            name="interests"
            rows={2}
            value={formData.interests}
            onChange={handleInputChange}
            placeholder="e.g., Coffee, gift cards, books, experiences…"
          />
        </Section>
      </div>

      <Button type="submit" size="lg" fullWidth className="mt-8">
        Continue to Assessment
      </Button>
    </form>
  );
};

export default ProfileSetupStep;
